import { useEffect, useState } from "react";
import { adminApi, errMsg } from "../api";
import { Field, TextInput, TextArea, Toggle, UploadButton, SaveBar } from "../components";
import { toast } from "../../components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.get("/settings").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!s) return <div className="adm-empty">Loading…</div>;

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setContact = (k, v) => setS((x) => ({ ...x, contact: { ...x.contact, [k]: v } }));
  const setSocial = (k, v) => setS((x) => ({ ...x, social: { ...x.social, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { id, key, ...payload } = s;
      await adminApi.put("/admin/settings", payload);
      toast.success("Settings saved — live on the public site");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const mediaUrl = (p) => (p?.startsWith("/api/media/") ? `${BACKEND_URL}${p}` : p);

  return (
    <div data-testid="admin-settings-page">
      <div className="adm-head">
        <div><h1>Site Settings</h1><div className="sub">Identity, contact details, branding</div></div>
        <SaveBar onSave={save} saving={saving} testid="save-settings" />
      </div>

      <div className="adm-card">
        <h2>Identity</h2>
        <div className="adm-grid cols-2">
          <Field label="Company name"><TextInput value={s.name} onChange={(v) => set("name", v)} data-testid="setting-name" /></Field>
          <Field label="Short tagline (under logo)"><TextInput value={s.tagline} onChange={(v) => set("tagline", v)} data-testid="setting-tagline" /></Field>
          <Field label="Full tagline" hint="hero + footer"><TextInput value={s.tagline_full} onChange={(v) => { set("tagline_full", v); set("footer_tagline", v); }} data-testid="setting-tagline-full" /></Field>
          <Field label="Header CTA label"><TextInput value={s.cta_label} onChange={(v) => set("cta_label", v)} data-testid="setting-cta-label" /></Field>
        </div>
        <Field label="Hero eyebrow"><TextInput value={s.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} /></Field>
        <div className="adm-grid cols-2">
          <Field label="Hero headline line 1"><TextInput value={s.hero_headline_line1} onChange={(v) => set("hero_headline_line1", v)} /></Field>
          <Field label="Hero headline accent"><TextInput value={s.hero_headline_accent} onChange={(v) => set("hero_headline_accent", v)} /></Field>
        </div>
        <Field label="Hero lead"><TextArea value={s.hero_lead} onChange={(v) => set("hero_lead", v)} rows={2} /></Field>
        <Field label="Footer description"><TextArea value={s.footer_description} onChange={(v) => set("footer_description", v)} rows={2} /></Field>
        <Field label="Canonical base URL" hint="used for canonical tags, sitemap & structured data"><TextInput value={s.canonical_base_url} onChange={(v) => set("canonical_base_url", v)} data-testid="setting-canonical-base" /></Field>
        <Field label="Commitment statement" hint="shown on the home page and Who We Are"><TextArea value={s.commitment_text} onChange={(v) => set("commitment_text", v)} rows={2} data-testid="setting-commitment" /></Field>
        <Field label="Commitment tagline" hint="sentence-case tagline under the commitment"><TextInput value={s.commitment_tagline} onChange={(v) => set("commitment_tagline", v)} data-testid="setting-commitment-tagline" /></Field>
      </div>

      <div className="adm-card">
        <h2>Feature photos</h2>
        <div className="adm-grid cols-2">
          <Field label="Home page photo" hint="shown in the Who We Are section">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {s.home_photo_media_id && <img className="thumb" src={mediaUrl(`/api/media/${s.home_photo_media_id}`)} alt="home feature" />}
              <UploadButton label="Upload photo" accept="image/*" kind="photo" testid="upload-home-photo"
                onUploaded={(m) => set("home_photo_media_id", m.id)} />
            </div>
          </Field>
          <Field label="Home photo alt text"><TextInput value={s.home_photo_alt} onChange={(v) => set("home_photo_alt", v)} data-testid="setting-home-photo-alt" /></Field>
          <Field label="About (Who We Are) photo" hint="shown on the Who We Are page">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {s.about_photo_media_id && <img className="thumb" src={mediaUrl(`/api/media/${s.about_photo_media_id}`)} alt="about feature" />}
              <UploadButton label="Upload photo" accept="image/*" kind="photo" testid="upload-about-photo"
                onUploaded={(m) => set("about_photo_media_id", m.id)} />
            </div>
          </Field>
          <Field label="About photo alt text"><TextInput value={s.about_photo_alt} onChange={(v) => set("about_photo_alt", v)} data-testid="setting-about-photo-alt" /></Field>
        </div>
      </div>

      <div className="adm-card">
        <h2>Contact information</h2>
        <div className="adm-grid cols-2">
          <Field label="Website"><TextInput value={s.contact?.website} onChange={(v) => setContact("website", v)} data-testid="setting-website" /></Field>
          <Field label="Email"><TextInput value={s.contact?.email} onChange={(v) => setContact("email", v)} data-testid="setting-email" /></Field>
          <Field label="Phone (display)"><TextInput value={s.contact?.phone} onChange={(v) => setContact("phone", v)} data-testid="setting-phone" /></Field>
          <Field label="Phone (tel: link)"><TextInput value={s.contact?.phone_href} onChange={(v) => setContact("phone_href", v)} /></Field>
          <Field label="Location line"><TextInput value={s.contact?.location} onChange={(v) => setContact("location", v)} /></Field>
          <Field label="Working hours"><TextInput value={s.contact?.hours} onChange={(v) => setContact("hours", v)} /></Field>
        </div>
      </div>

      <div className="adm-card">
        <h2>Social links</h2>
        <div className="adm-grid cols-2">
          {["linkedin", "twitter", "facebook", "instagram"].map((k) => (
            <Field key={k} label={k[0].toUpperCase() + k.slice(1)}><TextInput value={s.social?.[k]} onChange={(v) => setSocial(k, v)} placeholder="https://…" /></Field>
          ))}
        </div>
      </div>

      <div className="adm-card">
        <h2>Branding</h2>
        <div className="adm-grid cols-2">
          <Field label="Logo" hint="transparent PNG recommended">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img className="thumb" src={mediaUrl(s.logo_path)} alt="logo" style={{ background: "#141436", padding: 6 }} />
              <UploadButton label="Upload new logo" accept="image/*" kind="branding" testid="upload-logo"
                onUploaded={(m) => set("logo_path", `/api/media/${m.id}`)} />
            </div>
          </Field>
          <Field label="Favicon">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img className="thumb" src={mediaUrl(s.favicon_path)} alt="favicon" />
              <UploadButton label="Upload favicon" accept="image/png,image/x-icon,image/svg+xml" kind="branding" testid="upload-favicon"
                onUploaded={(m) => set("favicon_path", `/api/media/${m.id}`)} />
            </div>
          </Field>
        </div>
        <div className="adm-grid cols-2">
          <Field label="Watermark">
            <Toggle checked={s.watermark_enabled !== false} onChange={(v) => set("watermark_enabled", v)} label={s.watermark_enabled !== false ? "Enabled" : "Disabled"} testid="setting-watermark" />
          </Field>
          <Field label="Watermark opacity" hint="0.01 – 0.3">
            <TextInput type="number" value={s.watermark_opacity} onChange={(v) => set("watermark_opacity", Number(v))} data-testid="setting-watermark-opacity" />
          </Field>
        </div>
      </div>

      <SaveBar onSave={save} saving={saving} testid="save-settings-bottom" />
    </div>
  );
}
